import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  effect,
  untracked,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LtmsLoaderComponent } from '../../ltms-loader/ltms-loader.component';
import { SentimentAnalysisModule } from '../sentiment-analysis.module';
import { LtmsEventHandlerService } from 'src/app/shared/services/ltms-event-handler/ltms-event-handler.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import {
  SENTIMENT_ANALYSIS_CONFIG,
  type SentimentSettingsTabKey,
} from '../sentiment-analysis.config';
import { SentimentAnalysisStatsService } from '../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';
import { AppStateService } from '../../../services/helpers/app-state.service';
import { FeedDetailPanelComponent } from '../../feed-detail-panel/feed-detail-panel.component';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';

import {
  type DashboardStats,
  type RawCommentsPageInfo,
  type SubThemeSentimentBreakdown,
  type SummaryTopIssueLine,
  type ThemeSentimentBreakdown,
  isSubThemeDistributionRowSuggested,
} from '../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';
import type { SentimentFilterValue, ViewModeValue } from '../sentiment-analysis-overview-home/theme-distribution-widget/theme-distribution-widget.component';
import type { HeatmapCellPayload } from '../sentiment-analysis-details-home/details-heatmap-widget/details-heatmap-widget.component';
import type { ThemeBreakupItem } from '../sentiment-analysis-details-home/details-theme-breakup-bars/details-theme-breakup-bars.component';
import type { SubThemeBarRow } from '../sentiment-analysis-details-home/details-subtheme-breakup/details-subtheme-breakup.component';
import type {
  HeatmapCohortFilterOption,
  HeatmapFeedbackItem,
} from '../sentiment-analysis-details-home/details-heatmap-analysis/details-heatmap-analysis.component';
import {
  heatmapCohortFilterOptionsFromProjectConfig,
  summaryGeneratorSentimentOptionsFromSentiments,
  summaryPrecomputeSentimentsFromProjectConfig,
} from '../sentiment-analysis-setup.model';
import { SentimentAnalysisOverviewHomeComponent } from '../sentiment-analysis-overview-home/sentiment-analysis-overview-home.component';
import { SentimentAnalysisDetailsHomeComponent } from '../sentiment-analysis-details-home/sentiment-analysis-details-home.component';
import { SentimentAnalysisSummaryHomeComponent } from '../sentiment-analysis-summary-home/sentiment-analysis-summary-home.component';
import { SentimentAnalysisSettingsHomeComponent } from '../sentiment-analysis-settings-home/sentiment-analysis-settings-home.component';
import { SentimentAnalysisGettingStartedComponent } from '../sentiment-analysis-getting-started/sentiment-analysis-getting-started.component';

/** Gate before loading stats: insights config must exist or show Getting Started setup. */
type SentimentConfigGate = 'idle' | 'checking' | 'ready' | 'setup_required';

@Component({
  selector: 'app-sentiment-analysis',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LtmsLoaderComponent,
    SentimentAnalysisModule,
    FeedDetailPanelComponent,
    SentimentAnalysisOverviewHomeComponent,
    SentimentAnalysisDetailsHomeComponent,
    SentimentAnalysisSummaryHomeComponent,
    SentimentAnalysisSettingsHomeComponent,
    SentimentAnalysisGettingStartedComponent,
  ],
  templateUrl: './sentiment-analysis-home.component.html',
  styleUrls: ['./sentiment-analysis.layout.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SentimentAnalysisHomeComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private statsService = inject(SentimentAnalysisStatsService);
  private appStateService = inject(AppStateService);
  private accountsService = inject(AccountsService);
  private helpersService = inject(HelpersService);

  readonly config = SENTIMENT_ANALYSIS_CONFIG;

  projectId: string = '';
  accountId: string = '';
  /** brandId for API calls — from `AppStateService.brandDetails` (same as insights `brand_id`). */
  brandIdForApi: string = '';
  /** Passed to Getting Started for cohort fields and rating scale. */
  get brandDetailsForSetup(): Record<string, unknown> | null {
    const bd = this.appStateService.brandDetails();
    return bd && typeof bd === 'object' ? (bd as Record<string, unknown>) : null;
  }
  stats: DashboardStats | null = null;
  loading = true;
  /** Insights-only: blocks stats until `latest_sentiment_config` has taxonomy. */
  configGate: SentimentConfigGate = 'idle';
  activeTab: string = this.config.defaultTab;
  /**
   * Same pattern as `project-engagement-home`: gate tabs via `Helpers.isTabVisible`
   * against `module_configuration_v2.sentiment_analysis` sub-modules.
   */
  /** Same pattern as `project-inbox-home` `tabVisblity` — fixed tab buttons, no `*ngFor` list. */
  tabVisiblity: Record<'overview' | 'details' | 'summary', boolean> = {
    overview: true,
    details: true,
    summary: true,
  };
  /** `sentiment_analysis.sub_modules.feed_link_redirect` — open-in-new (launch) on feedback rows. */
  showSentimentFeedLinkRedirect = true;
  /** `sentiment_analysis.sub_modules.cohort_footer` — cohort chip row under feedback quotes. */
  showSentimentCohortFooter = true;

  /** Admin rule `SENTIMENT_ANALYSIS_MODULE_SETTINGS` (assign in Admin Panel) — gear + optional write. */
  sentimentSettingsPanelAllowed = false;
  sentimentSettingsCanWrite = false;
  @ViewChild(SentimentAnalysisSettingsHomeComponent)
  private sentimentAnalysisSettingsHome?: SentimentAnalysisSettingsHomeComponent;
  private statsLoadSub?: Subscription;
  private configCheckSub?: Subscription;
  /** Cancel in-flight summary generation so a slower response cannot repopulate after date/brand/theme change. */
  private summaryGenSub?: Subscription;
  /** Cancel in-flight heatmap request so a slower response cannot overwrite data for a different cohort. */
  private heatmapLoadSub?: Subscription;
  /** Incremented on each `loadHeatmapData` so a late/stale HTTP `next` cannot clear loading or overwrite data. */
  private heatmapLoadGeneration = 0;

  themeDistributionSentimentFilter: SentimentFilterValue = 'all';
  themeDistributionViewMode: ViewModeValue = 'themes';

  summaryOnlyActionable = true;
  summarySelectedSentiment = '';
  /** Summary generator dropdown: `config.summary.precompute.sentiments`. */
  summaryGeneratorSentimentOptions: Array<{ value: string; label: string }> = [];
  summarySelectedThemeId = '';
  summaryText: string | null = null;
  summaryStats: { feedbackCount: number; actionableCount: number } | null = null;
  /** v2 API: `facts.top_subthemes` + cohort fact (shown after main summary paragraph). */
  summaryTopIssues: SummaryTopIssueLine[] = [];
  /** v2 API: `summary.focus_areas`. */
  summaryNextSteps: string[] = [];
  summaryGenerating = false;
  summaryLoading = false;

  detailsTimePeriod: string = this.config.defaultTimePeriod;
  /** Trend granularity dropdown; **Daily** removed when range exceeds `config.trendDailyMaxInclusiveDays`. */
  detailsTrendTimePeriodOptions: Array<{ value: string; label: string }> = (
    SENTIMENT_ANALYSIS_CONFIG.timePeriods as unknown as Array<{ value: string; label: string }>
  ).map((o) => ({ ...o }));
  /** From global datePicker `trendType` — drives POST /api/summary/v2/run `period_type`. */
  summaryV2PeriodType: 'week' | 'month' | 'quarter' = 'month';
  detailsTrendThemeId: string | null = null;
  detailsTrendSubThemeId: string | null = null;
  trendData: Array<{ period: string; count: number }> = [];
  trendLoading = false;
  /** Cohort dimension `field_id` from insights `heatmap_v2.cohorts`; empty until options exist. */
  detailsHeatmapCohortType = '';
  /** Category Heatmap cohort dropdown: keys from `config.stats.widgets.heatmap_v2.cohorts`. */
  heatmapCohortOptions: HeatmapCohortFilterOption[] = [];
  /** Latest insights project config (for heatmap cohort dropdown). */
  private insightsProjectConfig: Record<string, unknown> | null = null;
  detailsHeatmapLevel: 'theme' | 'subTheme' = 'theme';
  detailsHeatmapSentimentType: 'negative' | 'non_negative' = 'negative';
  detailsHeatmapThemeId: string | null = null;
  heatmapData: any[] = [];
  heatmapLoading = false;
  heatmapCellSelected: HeatmapCellPayload | null = null;
  heatmapFeedbackList: HeatmapFeedbackItem[] = [];
  heatmapFeedbackLoading = false;
  /** Insights engine: paginated raw comments for heatmap cell drill-down. */
  heatmapFeedbackLoadingMore = false;
  heatmapFeedbackHasMore = false;
  heatmapFeedbackPage = 1;
  heatmapFeedbackThemeId: string | null = null;
  heatmapFeedbackSubThemeId: string | null = null;
  /** Mirrors POST /api/stats/v1/raw_comments `filters.sentiment_labels` when heatmap column is theme sentiment. */
  private heatmapFeedbackRawFilters: { sentiment_labels?: string[] } = {};
  private heatmapFeedbackRawAccumulated: any[] = [];
  /**
   * Raw comments are loaded by theme (paginated). Cohort is applied in the browser, so the first page
   * often contains only one row matching the clicked cell. Auto-fetch more pages until we have enough
   * matches or the API is exhausted.
   */
  private heatmapFeedbackAutoPrefetchRounds = 0;
  private readonly heatmapFeedbackAutoPrefetchMaxRounds = 24;
  /** Used to reset heatmap cohort to the first dropdown option when the project/brand changes. */
  private lastHeatmapCohortBrandId = '';
  /** Detects config-driven cohort list updates (e.g. placeholder `rating_bucket` only → full list). */
  private lastHeatmapCohortOptionsSignature = '';
  detailsSubthemeSelectedThemeId = '';
  detailsTrendCohortTab: 'trend' | 'cohort' = 'trend';
  /** Cohort tab: cohort dimension (`field_id` or built-in key); options match heatmap cohorts except `rating_bucket`. */
  /** Set from first `field_definitions` cohort option via `ensureDetailsCohortDimensionInDropdown`. */
  detailsCohortCardDimension = '';
  /** Categorized feedback for cohort table (loaded when Cohort tab + theme/sub-theme selected). */
  cohortCardFeedback: any[] = [];
  cohortCardTableLoading = false;
  cohortCardFeedbackPage = 1;
  cohortCardFeedbackHasMore = false;
  cohortCardFeedbackLoadingMore = false;
  /** Cohort table rows derived from cohortCardFeedback by detailsCohortCardDimension. */
  cohortCardTableData: Array<{ name: string; positive: number; neutral: number; negative: number }> = [];
  themeBreakupPanelOpen = false;
  themeBreakupPanelTheme: ThemeBreakupItem | null = null;
  /** Cached sub-themes for the open theme breakup panel; avoids calling getSubThemesForThemeBreakupPanel in template every CD. */
  themeBreakupPanelSubThemes: SubThemeSentimentBreakdown[] = [];
  /** Theme side panel: Sub-themes list — suggested-only vs non-suggested (default false = non-suggested). */
  themePanelShowSuggestedSubthemesOnly = false;
  subThemeBreakupPanelOpen = false;
  subThemeBreakupPanelSubTheme: SubThemeBarRow | null = null;
  /** Theme ID for the open sub-theme panel (for loading feedback; set when opening from theme panel or card). */
  subThemeBreakupPanelThemeId: string | null = null;

  /** Customer feedback for Theme side panel (loaded on open). */
  themePanelFeedback: any[] = [];
  themePanelFeedbackLoading = false;
  themePanelFeedbackLoadingMore = false;
  themePanelFeedbackHasMore = false;
  themePanelFeedbackPage = 1;
  themePanelFeedbackTotalCount: number | null = null;
  /** Customer feedback for Sub-theme side panel (loaded on open). */
  subThemePanelFeedback: any[] = [];
  subThemePanelFeedbackLoading = false;
  subThemePanelFeedbackLoadingMore = false;
  subThemePanelFeedbackHasMore = false;
  subThemePanelFeedbackPage = 1;
  subThemePanelFeedbackTotalCount: number | null = null;

  /** Saved scroll position to restore after closing side panel. */
  private savedScrollPosition: { x: number; y: number; scrollContainerTop: number } = { x: 0, y: 0, scrollContainerTop: 0 };

  /** ID of the main scroll container in project layout (overflow-y: auto); restore its scrollTop to prevent page jump. */
  private readonly mainScrollContainerId = 'projectRouterOutlet';

  /** Summary tab theme dropdown: non-suggested only (`is_suggested_theme` false), same rule as heatmap. */
  cachedSummaryThemes: Array<{ themeId: string; themeName: string }> = [];
  /** Category heatmap theme dropdown: non-suggested themes only (`is_suggested_theme` false). */
  cachedHeatmapThemes: Array<{ themeId: string; themeName: string }> = [];

  constructor(    
    private _ltmsEvent: LtmsEventHandlerService,
  ) {
    /**
     * Track AppStateService `brandDetails` + `datePicker` (global date picker) for insights engine,
     * or route query params for legacy APIs. See `app-state.service.ts` usage notes.
     */
    effect(() => {
      const bd = this.appStateService.brandDetails();
      const dp = this.appStateService.datePicker() as Record<string, unknown>;
      /** Re-fetch insights when global hierarchy changes (`HelpersService` syncs into app state). */
      void this.appStateService.hierarchyStack();
      untracked(() => {
        this.syncHeatmapCohortOptions();
        this.syncSummaryGeneratorSentimentOptions();
        this.refreshSentimentTabVisibilityAndList();
        this.activeTab = this.clampSentimentActiveTab(this.activeTab || this.config.defaultTab);
        this.syncTrendTimePeriodOptionsFromDatePicker(dp);
      });

      if (this.config.useInsightsEngine) {
        const trendPeriod = SentimentAnalysisHomeComponent.mapDatePickerTrendTypeToV2Period(dp?.['trendType']);
        const brandId = String(_.get(bd, '_id', '') || '').trim();
        const startYmd = dp?.['startDate'];
        const endYmd = dp?.['endDate'];

        if (!brandId || startYmd == null || endYmd == null || String(startYmd) === '' || String(endYmd) === '') {
          untracked(() => {
            this.summaryV2PeriodType = trendPeriod;
            this.brandIdForApi = brandId;
            this.loading = false;
            this.cdr.markForCheck();
          });
          return;
        }

        const range = SentimentAnalysisHomeComponent.datePickerYmdToIsoRange(
          String(startYmd),
          String(endYmd)
        );
        if (!range) {
          untracked(() => {
            this.summaryV2PeriodType = trendPeriod;
            this.brandIdForApi = brandId;
            this.loading = false;
            this.cdr.markForCheck();
          });
          return;
        }

        untracked(() => {
          this.summaryV2PeriodType = trendPeriod;
          this.brandIdForApi = brandId;
          this.projectId = brandId;
          this.statsService.setInsightsTimeRange(range);
          this.resolveConfigAndLoadStats(brandId);
        });
        return;
      }

      const brand = String(_.get(bd, '_id', '') || '').trim();

      if (!brand) {
        untracked(() => {
          this.brandIdForApi = '';
          this.configGate = 'idle';
          this.loading = false;
          this.cdr.markForCheck();
        });
        return;
      }

      untracked(() => {
        this.statsService.setInsightsTimeRange(null);
        this.brandIdForApi = brand;
        this.configGate = 'ready';
        this.runStatsLoad(brand);
      });
    });
  }

  /**
   * Sub-module keys present for Sentiment (after `getFlatenModuleConfig` + `tab_mappings`).
   * Legacy: `summary` / `details` / `verbatim` (UI: Overview / Details / Summary).
   * Current: `overview` / `details` / `summary`.
   */
  private getSentimentSubMenuKeys(): string[] {
    const h = typeof Helpers !== 'undefined' ? (Helpers as any) : (window as any).Helpers;
    const sm = _.get(h, 'moduleConfigs.project.subMenu.sentiment_analysis', {}) as Record<string, unknown>;
    return Object.keys(sm || {});
  }

  /** Any extra `sentiment_analysis` sub-module flags (not main tabs), e.g. `feed_link_redirect`, `cohort_footer`. */
  private sentimentSubModuleSignalVisible(subKey: string): boolean {
    const h = typeof Helpers !== 'undefined' ? (Helpers as any) : (window as any).Helpers;
    if (!h?.isTabVisible) {
      return true;
    }
    return h.isTabVisible(false, 'sentiment_analysis', subKey);
  }

  private sentimentUiTabVisible(uiKey: 'overview' | 'details' | 'summary'): boolean {
    const h = typeof Helpers !== 'undefined' ? (Helpers as any) : (window as any).Helpers;
    if (!h?.isTabVisible) {
      return true;
    }
    const keys = this.getSentimentSubMenuKeys();
    const legacyLayout = keys.includes('verbatim');

    const feat = (k: 'overview' | 'details' | 'summary') => {
      if (k === 'overview') return Boolean((this.config.features as any).showSummaryTab);
      if (k === 'details') return Boolean((this.config.features as any).showDetailsTab);
      return Boolean((this.config.features as any).showVerbatimTab);
    };

    if (uiKey === 'details') {
      return feat('details') && h.isTabVisible(false, 'sentiment_analysis', 'details');
    }
    if (uiKey === 'overview') {
      if (legacyLayout) {
        return feat('overview') && h.isTabVisible(false, 'sentiment_analysis', 'summary');
      }
      return feat('overview') && h.isTabVisible(false, 'sentiment_analysis', 'overview');
    }
    // uiKey === 'summary' (generator / “Summary” tab)
    if (legacyLayout) {
      return feat('summary') && h.isTabVisible(false, 'sentiment_analysis', 'verbatim');
    }
    return feat('summary') && h.isTabVisible(false, 'sentiment_analysis', 'summary');
  }

  /** Same idea as engagement / inbox: recompute `tabVisblity` only (tab DOM stays stable). */
  private refreshSentimentTabVisibilityAndList(): void {
    this.tabVisiblity = {
      overview: this.sentimentUiTabVisible('overview'),
      details: this.sentimentUiTabVisible('details'),
      summary: this.sentimentUiTabVisible('summary'),
    };

    this.showSentimentFeedLinkRedirect = this.sentimentSubModuleSignalVisible('feed_link_redirect');
    this.showSentimentCohortFooter = this.sentimentSubModuleSignalVisible('cohort_footer');
    this.refreshSentimentSettingsAdminFlags();
  }

  /** Inbox-style: each tab is its own element; when all flags are off, show all three (legacy fallback). */
  isSentimentTabShown(uiKey: 'overview' | 'details' | 'summary'): boolean {
    if (this.tabVisiblity[uiKey]) {
      return true;
    }
    return !this.tabVisiblity.overview && !this.tabVisiblity.details && !this.tabVisiblity.summary;
  }

  private allowedSentimentTabKeys(): string[] {
    const ordered: Array<{ uiKey: 'overview' | 'details' | 'summary'; configKey: string }> = [
      { uiKey: 'overview', configKey: this.config.tabs.overview.key },
      { uiKey: 'details', configKey: this.config.tabs.details.key },
      { uiKey: 'summary', configKey: this.config.tabs.summary.key },
    ];
    const visible = ordered.filter((t) => this.isSentimentTabShown(t.uiKey)).map((t) => t.configKey);
    return visible.length ? visible : ordered.map((t) => t.configKey);
  }

  /** `Helpers.adminRulesDic` from Admin Panel rules — key `SENTIMENT_ANALYSIS_MODULE_SETTINGS`. */
  private refreshSentimentSettingsAdminFlags(): void {
    const H = typeof Helpers !== 'undefined' ? (Helpers as any) : (window as any).Helpers;
    const d = _.get(H, 'adminRulesDic.SENTIMENT_ANALYSIS_MODULE_SETTINGS');
    this.sentimentSettingsPanelAllowed = !!(d?.read || d?.write);
    this.sentimentSettingsCanWrite = !!d?.write;
  }

  openSentimentSettingsPanel(opts?: {
    setupMode?: boolean;
    initialTab?: SentimentSettingsTabKey;
  }): void {
    if (!this.sentimentSettingsPanelAllowed) {
      return;
    }
    this.sentimentAnalysisSettingsHome?.openPanel(opts);
  }

  onSentimentSettingsSaved(brandId: string): void {
    this.applyBrandAndModuleConfigFromServer(brandId);
    if (this.brandIdForApi && this.config.useInsightsEngine) {
      this.statsService.clearSentimentConfigCache(this.brandIdForApi);
      this.resolveConfigAndLoadStats(this.brandIdForApi, { forceConfigCheck: true });
    }
  }

  onSentimentConfigurationSaved(brandId: string): void {
    this.statsService.clearSentimentConfigCache(brandId);
    this.resolveConfigAndLoadStats(brandId, { forceConfigCheck: true });
  }

  onGettingStartedConfigReady(): void {
    if (this.brandIdForApi) {
      this.onSentimentConfigurationSaved(this.brandIdForApi);
    }
  }

  /**
   * Insights engine: GET latest config first; legacy mode loads stats directly.
   */
  private resolveConfigAndLoadStats(
    brandId: string,
    opts?: { forceConfigCheck?: boolean; forceRefresh?: boolean },
  ): void {
    if (!this.config.useInsightsEngine || !this.config.insightsLatestSentimentConfigPath) {
      this.configGate = 'ready';
      this.runStatsLoad(brandId, { forceRefresh: opts?.forceRefresh });
    } else {
      this.configGate = 'checking';
      this.loading = true;
      this.cdr.markForCheck();
      this.configCheckSub?.unsubscribe();
      this.configCheckSub = this.statsService
        .getLatestSentimentConfig(brandId, { force: opts?.forceConfigCheck })
        .subscribe({
          next: (result) => {
            this.insightsProjectConfig = (result.document?.config ?? null) as Record<
              string,
              unknown
            > | null;
            this.syncHeatmapCohortOptions();
            this.syncSummaryGeneratorSentimentOptions();
            if (result.ready) {
              this.configGate = 'ready';
              this.runStatsLoad(brandId, { forceRefresh: opts?.forceRefresh });
            } else {
              this.configGate = 'setup_required';
              this.loading = false;
              this.applyBrandAndModuleConfigFromServer(brandId);
              this.cdr.markForCheck();
            }
          },
          error: () => {
            this.insightsProjectConfig = null;
            this.syncHeatmapCohortOptions();
            this.syncSummaryGeneratorSentimentOptions();
            this.configGate = 'setup_required';
            this.loading = false;
            this.showSnackbar(this.config.copy.errorLoad, 'error');
            this.cdr.markForCheck();
          },
        });
    }
  }

  /** Re-fetch brand + module config and refresh `Helpers.moduleConfigs` (same idea as project load). */
  private applyBrandAndModuleConfigFromServer(brandId: string): void {
    forkJoin({
      brandDetails: this.accountsService.getBrandDetail(brandId),
      moduleConfig: this.accountsService.getModulConfigs({ brand_id: brandId }),
    }).subscribe({
      next: (response: any) => {
        if (_.get(response, 'brandDetails.code') !== 0 || _.isEmpty(_.get(response, 'brandDetails.data.brand'))) {
          this.refreshSentimentTabVisibilityAndList();
          this.cdr.markForCheck();
          return;
        }
        let brandDetails = _.get(response, 'brandDetails.data.brand');
        let moduleConfigs: any = {};
        if (_.get(response, 'moduleConfig.data.code') === 0 && !_.isEmpty(_.get(response, 'moduleConfig.data.module_configuration_v2'))) {
          moduleConfigs = _.get(response, 'moduleConfig.data.module_configuration_v2');
        }
        const moduleConfig = !_.isEmpty(moduleConfigs) ? moduleConfigs : brandDetails.module_configuration_v2;
        const module_configuration_v2 = this.helpersService.getFlatenModuleConfig(moduleConfig, false);
        this.helpersService.helpers['moduleConfigs'] = module_configuration_v2;
        brandDetails = { ...brandDetails, module_configuration_v2: moduleConfig };
        this.appStateService.updateBrandDetails(brandDetails);
        window['Helpers'] = this.helpersService.consoleData();
        this.refreshSentimentTabVisibilityAndList();
        this.activeTab = this.clampSentimentActiveTab(this.activeTab);
        this.cdr.markForCheck();
      },
      error: () => {
        this.refreshSentimentTabVisibilityAndList();
        this.cdr.markForCheck();
      },
    });
  }

  private clampSentimentActiveTab(key: string): string {
    const allowed = this.allowedSentimentTabKeys();
    if (allowed.length && !allowed.includes(key)) {
      return allowed[0] ?? this.config.defaultTab;
    }
    return key;
  }

  /**
   * Maps `active_tab` query to a tab key.
   * Back-compat: legacy `active_tab=summary` was Overview; legacy `active_tab=verbatim` was the generator tab.
   */
  private resolveActiveTabFromQuery(raw: string | null | undefined): string {
    const v = String(raw ?? '').trim();
    if (!v) {
      return this.config.defaultTab;
    }
    const lower = v.toLowerCase();
    const keys = this.getSentimentSubMenuKeys();
    const legacyLayout = keys.includes('verbatim');

    if (lower === 'verbatim') {
      return this.config.tabs.summary.key;
    }
    if (lower === 'summary') {
      return legacyLayout ? this.config.tabs.overview.key : this.config.tabs.summary.key;
    }

    const tabs = [this.config.tabs.overview, this.config.tabs.details, this.config.tabs.summary];

    // Canonical keys written by setActiveTab (always lowercase from config)
    if (v === 'overview' || v === 'details' || v === 'summary') {
      return v;
    }
    for (const t of tabs) {
      if (t.label.toLowerCase() === lower) {
        return t.key;
      }
    }
    if (lower === 'overview' || lower === 'details' || lower === 'summary') {
      return lower;
    }
    return this.config.defaultTab;
  }

  /** Insights: `heatmap_v2.cohorts`; legacy: brand `show_in_sentiment_cohort` fields + rating bucket. */
  private syncHeatmapCohortOptions(): void {
    const bd = this.appStateService.brandDetails() as Record<string, unknown>;
    const brandId = String(_.get(bd, '_id', '') || '').trim();
    let out: HeatmapCohortFilterOption[] = [];

    if (this.config.useInsightsEngine) {
      out = heatmapCohortFilterOptionsFromProjectConfig(this.insightsProjectConfig, bd);
    } else {
      out = this.heatmapCohortOptionsFromBrandFlags(bd);
    }

    this.heatmapCohortOptions = out;

    if (out.length === 0) {
      this.detailsHeatmapCohortType = '';
      this.lastHeatmapCohortBrandId = brandId;
      this.ensureDetailsCohortDimensionInDropdown();
      this.cdr.markForCheck();
      return;
    }

    const defaultCohort = out[0].value;
    const current = String(this.detailsHeatmapCohortType ?? '').trim();
    const selectionInList = current !== '' && out.some((o) => o.value === current);
    const brandSwitched = brandId !== '' && brandId !== this.lastHeatmapCohortBrandId;

    if (brandSwitched) {
      this.lastHeatmapCohortOptionsSignature = '';
    }

    const prevOptionsSignature = this.lastHeatmapCohortOptionsSignature;
    const optionsSignature = out.map((o) => o.value).join('\0');
    const optionsChanged = optionsSignature !== prevOptionsSignature;
    this.lastHeatmapCohortOptionsSignature = optionsSignature;

    if (brandId) {
      this.lastHeatmapCohortBrandId = brandId;
    } else {
      this.lastHeatmapCohortBrandId = '';
    }

    const wasRatingBucketPlaceholder =
      prevOptionsSignature === '' || prevOptionsSignature === 'rating_bucket';
    const shouldDefaultToFirst =
      !selectionInList ||
      brandSwitched ||
      (optionsChanged &&
        defaultCohort !== 'rating_bucket' &&
        current === 'rating_bucket' &&
        wasRatingBucketPlaceholder);

    if (shouldDefaultToFirst) {
      this.detailsHeatmapCohortType = defaultCohort;
    }

    this.ensureDetailsCohortDimensionInDropdown();
    this.cdr.markForCheck();
  }

  /** Summary tab sentiment dropdown from `summary.precompute.sentiments`. */
  private syncSummaryGeneratorSentimentOptions(): void {
    let sentimentIds: string[];
    if (this.config.useInsightsEngine) {
      sentimentIds = summaryPrecomputeSentimentsFromProjectConfig(this.insightsProjectConfig);
    } else {
      sentimentIds = ['positive', 'negative', 'neutral'];
    }
    this.summaryGeneratorSentimentOptions = summaryGeneratorSentimentOptionsFromSentiments(sentimentIds);

    const current = String(this.summarySelectedSentiment ?? '').trim();
    const selectionInList =
      current !== '' && this.summaryGeneratorSentimentOptions.some((o) => o.value === current);
    if (!selectionInList && this.summaryGeneratorSentimentOptions.length) {
      this.summarySelectedSentiment = this.summaryGeneratorSentimentOptions[0].value;
      this.discardSummaryGeneratorResults(true);
    }
    this.cdr.markForCheck();
  }

  /** Legacy sentiment APIs: brand `field_definitions` with `show_in_sentiment_cohort`. */
  private heatmapCohortOptionsFromBrandFlags(
    bd: Record<string, unknown>,
  ): HeatmapCohortFilterOption[] {
    const defs = _.get(bd, 'field_definitions', []) as unknown;
    const list = Array.isArray(defs) ? defs : [];
    const seen = new Set<string>();
    const out: HeatmapCohortFilterOption[] = [];

    for (const raw of list) {
      const fd = raw as Record<string, unknown>;
      if (!fd || typeof fd !== 'object') continue;
      const flag = fd['show_in_sentiment_cohort'] ?? fd['showInSentimentCohort'];
      if (flag !== true && flag !== 'true' && flag !== 1) continue;
      const value = String(fd['field_id'] ?? fd['fieldId'] ?? '').trim();
      if (!value || seen.has(value)) continue;
      seen.add(value);
      const label = String(
        fd['field_display_name'] ??
          fd['fieldDisplayName'] ??
          fd['display_name'] ??
          fd['displayName'] ??
          fd['name'] ??
          fd['label'] ??
          value,
      ).trim();
      out.push({ value, label: label || value });
    }

    if (!out.some((o) => o.value === 'rating_bucket')) {
      out.push({ value: 'rating_bucket', label: 'Rating Bucket' });
    }
    return out;
  }

  /** Cohort-wise break-up dropdown: heatmap cohorts (excluding Rating Bucket when present). */
  get cohortBreakdownDropdownOptions(): Array<{ value: string; label: string }> {
    return this.heatmapCohortOptions.filter((o) => o.value !== 'rating_bucket');
  }

  private ensureDetailsCohortDimensionInDropdown(): void {
    const opts = this.cohortBreakdownDropdownOptions;
    if (!opts.length) {
      return;
    }
    if (!opts.some((o) => o.value === this.detailsCohortCardDimension)) {
      this.detailsCohortCardDimension = opts[0].value;
    }
  }

  /**
   * Maps global header datePicker `trendType` (see `appliedDateFilters.trendType` / `activeRangeType.type`)
   * to summary v2 `period_type` (week | month | quarter only).
   */
  private static mapDatePickerTrendTypeToV2Period(trendType: unknown): 'week' | 'month' | 'quarter' {
    const t = String(trendType ?? '').trim().toLowerCase();
    const map: Record<string, 'week' | 'month' | 'quarter'> = {
      day: 'week',
      week: 'week',
      month: 'month',
      quarter: 'quarter',
      year: 'quarter',
      financial_year: 'quarter',
      all: 'month',
    };
    return map[t] ?? 'month';
  }

  /** Converts AppState `datePicker.startDate` / `endDate` (YYYYMMDD) to inclusive ISO range for the insights API. */
  private static datePickerYmdToIsoRange(startYmd: string, endYmd: string): { from: string; to: string } | null {
    const parse = (raw: string, endOfDay: boolean): Date | null => {
      const digits = String(raw || '').replace(/\D/g, '');
      if (digits.length !== 8) return null;
      const y = Number(digits.slice(0, 4));
      const m = Number(digits.slice(4, 6)) - 1;
      const d = Number(digits.slice(6, 8));
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
      return endOfDay ? new Date(y, m, d, 23, 59, 59, 999) : new Date(y, m, d, 0, 0, 0, 0);
    };
    const fromD = parse(startYmd, false);
    const toD = parse(endYmd, true);
    if (!fromD || !toD || fromD > toD) return null;
    return { from: fromD.toISOString(), to: toD.toISOString() };
  }

  /** Inclusive calendar-day count for `YYYYMMDD` … `YYYYMMDD` (local date). */
  private static inclusiveDaySpanYmd(startYmd: string, endYmd: string): number {
    const parseMidnight = (raw: string): Date | null => {
      const digits = String(raw || '').replace(/\D/g, '');
      if (digits.length !== 8) return null;
      const y = Number(digits.slice(0, 4));
      const m = Number(digits.slice(4, 6)) - 1;
      const d = Number(digits.slice(6, 8));
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
      return new Date(y, m, d, 0, 0, 0, 0);
    };
    const fromD = parseMidnight(startYmd);
    const toD = parseMidnight(endYmd);
    if (!fromD || !toD || fromD > toD) return 0;
    return Math.round((toD.getTime() - fromD.getTime()) / 86400000) + 1;
  }

  /** Hides **Daily** in Details trend when global date range is wider than config. */
  private syncTrendTimePeriodOptionsFromDatePicker(dp: Record<string, unknown>): void {
    const startYmd = dp?.['startDate'];
    const endYmd = dp?.['endDate'];
    let span = 0;
    if (startYmd != null && endYmd != null && String(startYmd) !== '' && String(endYmd) !== '') {
      span = SentimentAnalysisHomeComponent.inclusiveDaySpanYmd(String(startYmd), String(endYmd));
    }
    const maxInclusive = (this.config as { trendDailyMaxInclusiveDays?: number }).trendDailyMaxInclusiveDays ?? 31;
    const hideDaily = span > maxInclusive;
    const base = this.config.timePeriods.map((o) => ({ value: o.value, label: o.label }));
    this.detailsTrendTimePeriodOptions = hideDaily ? base.filter((p) => p.value !== 'daily') : base;
    if (hideDaily && this.detailsTimePeriod === 'daily') {
      this.detailsTimePeriod = 'weekly';
    }
  }

  private runStatsLoad(
    brandId: string,
    opts?: { fullPageLoading?: boolean; forceRefresh?: boolean },
  ): void {
    const fullPageLoading = opts?.fullPageLoading !== false;
    const forceRefresh = opts?.forceRefresh === true;
    /** New stats window (date range / brand): drop prior generated summary so UI does not show stale copy. */
    this.discardSummaryGeneratorResults(true);
    if (fullPageLoading) {
      this.loading = true;
      this.cdr.markForCheck();
    }
    this.statsLoadSub?.unsubscribe();
    this.statsLoadSub = this.statsService.loadStatsOnce(brandId, { force: forceRefresh }).subscribe({
      next: () => {
        if (fullPageLoading) {
          this.loading = false;
        }
        if (this.activeTab === this.config.tabs.details.key) {
          this.refreshDetailsTabWidgets();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        if (fullPageLoading) {
          this.loading = false;
        }
        this.showSnackbar(this.config.copy.errorLoad, 'error');
        this.cdr.markForCheck();
      },
    });
  }

  /** Clear Customer Feedback drill-down when stats/time window changes; keeps heatmap filters/level intact. */
  /**
   * Details tab only: trend chart, heatmap, and cohort table — does not repeat the main stats bundle
   * (overview + theme/subtheme distribution). Call when opening Details or after stats refresh while Details is active.
   */
  private refreshDetailsTabWidgets(): void {
    if (!this.brandIdForApi) return;
    /** Ensure cohort dropdown options + default to first option before heatmap/trend load. */
    this.syncHeatmapCohortOptions();
    this.loadTrendData();
    if (
      this.detailsTrendCohortTab === 'cohort' &&
      (this.detailsTrendThemeId || this.detailsTrendSubThemeId)
    ) {
      this.loadCohortCardData();
    }
    if (this.config.features.showHeatmap) {
      this.clearHeatmapFeedbackPanelOnly();
      this.loadHeatmapData();
    }
  }

  private clearHeatmapFeedbackPanelOnly(): void {
    this.heatmapCellSelected = null;
    this.heatmapFeedbackList = [];
    this.heatmapFeedbackRawAccumulated = [];
    this.heatmapFeedbackPage = 1;
    this.heatmapFeedbackHasMore = false;
    this.heatmapFeedbackLoadingMore = false;
    this.heatmapFeedbackThemeId = null;
    this.heatmapFeedbackSubThemeId = null;
    this.heatmapFeedbackRawFilters = {};
    this.heatmapFeedbackLoading = false;
  }

  onHeatmapSentimentModeChange(next: 'negative' | 'non_negative'): void {
    if (this.detailsHeatmapSentimentType === next) {
      return;
    }
    this.detailsHeatmapSentimentType = next;
    /** Reset the "Customer Feedback" card when sentiment mode changes. */
    this.clearHeatmapFeedbackPanelOnly();
    this.loadHeatmapData();
  }

  ngOnInit(): void {
    this.refreshSentimentTabVisibilityAndList();

    const initialTab = this.route.snapshot.queryParamMap.get('active_tab');
    if (initialTab != null && String(initialTab).trim() !== '') {
      const resolved = this.resolveActiveTabFromQuery(initialTab);
      this.activeTab = this.clampSentimentActiveTab(resolved);
    } else {
      this.activeTab = this.clampSentimentActiveTab(this.activeTab || this.config.defaultTab);
      this.setActiveTab(this.activeTab);
    }

    this.statsService.stats$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((s) => {
      this.stats = s;
      this.refreshCaches();
      this.cdr.markForCheck();
    });

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.accountId = _.get(params, 'account_id', '') || '';
      if (!this.config.useInsightsEngine) {
        this.projectId = _.get(params, 'project_id', '') || '';
        this.brandIdForApi = String(_.get(this.appStateService.brandDetails(), '_id', '') || '').trim();
        if (!this.brandIdForApi) {
          this.loading = false;
        }
      }

      this.refreshSentimentTabVisibilityAndList();

      const rawTab = params['active_tab'];
      if (rawTab != null && String(rawTab).trim() !== '') {
        const resolved = this.resolveActiveTabFromQuery(String(rawTab));
        const clamped = this.clampSentimentActiveTab(resolved);
        if (clamped !== String(rawTab)) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { active_tab: clamped },
            queryParamsHandling: 'merge',
          });
        }
        if (clamped !== this.activeTab) {
          this.activeTab = clamped;
          if (this.brandIdForApi && this.activeTab === this.config.tabs.details.key) {
            this.refreshDetailsTabWidgets();
          }
        }
      } else {
        const clamped = this.clampSentimentActiveTab(this.activeTab || this.config.defaultTab);
        if (clamped !== this.activeTab) {
          this.activeTab = clamped;
        }
      }

      this.cdr.markForCheck();
    });

    if (this.brandIdForApi && this.activeTab === this.config.tabs.details.key) {
      this.refreshDetailsTabWidgets();
    }
  }

  ngOnDestroy(): void {
    this.statsLoadSub?.unsubscribe();
    this.summaryGenSub?.unsubscribe();
    this.heatmapLoadSub?.unsubscribe();
  }

  /** Refresh cached arrays used in template to avoid heavy work on every change detection. */
  private refreshCaches(): void {
    this.cachedSummaryThemes = this.getNonSuggestedThemeDropdownItems();
    this.cachedHeatmapThemes = this.getHeatmapThemeDropdownItems();
    const ids = new Set(this.cachedSummaryThemes.map((t) => t.themeId));
    if (this.summarySelectedThemeId && !ids.has(this.summarySelectedThemeId)) {
      this.summarySelectedThemeId = '';
      this.discardSummaryGeneratorResults(true);
    }
    const heatmapIds = new Set(this.cachedHeatmapThemes.map((t) => t.themeId));
    let heatmapThemeFilterCleared = false;
    if (this.detailsHeatmapThemeId && !heatmapIds.has(this.detailsHeatmapThemeId)) {
      this.detailsHeatmapThemeId = null;
      heatmapThemeFilterCleared = true;
    }
    if (heatmapThemeFilterCleared && this.config.features.showHeatmap && this.brandIdForApi) {
      queueMicrotask(() => this.loadHeatmapData());
    }
  }

  /** Clears generated summary UI; optionally aborts an in-flight generate request. */
  private discardSummaryGeneratorResults(cancelPendingRequest: boolean): void {
    if (cancelPendingRequest) {
      this.summaryGenSub?.unsubscribe();
      this.summaryGenSub = undefined;
    }
    this.summaryGenerating = false;
    this.summaryText = null;
    this.summaryStats = null;
    this.summaryTopIssues = [];
    this.summaryNextSteps = [];
  }

  onSummaryThemeIdChange(themeId: string): void {
    this.summarySelectedThemeId = themeId;
    this.discardSummaryGeneratorResults(true);
    this.cdr.markForCheck();
  }

  onSummarySentimentChange(sentiment: string): void {
    this.summarySelectedSentiment = sentiment;
    this.discardSummaryGeneratorResults(true);
    this.cdr.markForCheck();
  }

  setActiveTab(tabKey: string): void {
    const next = this.clampSentimentActiveTab(tabKey);
    if (next === this.activeTab) {
      return;
    }
    this.activeTab = next;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { active_tab: next },
      queryParamsHandling: 'merge',
    });
    
    /** Local state only — avoids `router.navigate` + query param churn that re-renders the side menu icons. */
    if (!this.brandIdForApi) {
      this.cdr.markForCheck();
      return;
    }
    /** Overview / Summary tabs use in-memory stats (refreshed on date/brand change via effect). No duplicate stats POSTs. */
    if (next === this.config.tabs.details.key) {
      this.refreshDetailsTabWidgets();
    }
    this.cdr.markForCheck();
  }

  getActionableComments(): number {
    return (this.stats?.actionableComments ?? 0) + (this.stats?.actionableMentions ?? 0);
  }

  /** For Details tab: total actionable feedback (from actionable mentions insights when available). */
  getDetailsActionableFeedback(): number {
    const insights = this.stats?.actionableMentionsInsights;
    if (insights?.actionableMentionsTotal != null) return insights.actionableMentionsTotal;
    return this.getActionableComments();
  }

  /** For Details tab: positive actionable count (overview `theme_sentiment`). */
  getDetailsPositiveActionable(): number {
    return this.stats?.themeSentiment?.positive?.count ?? 0;
  }

  /** For Details tab: neutral actionable count (overview `theme_sentiment`). */
  getDetailsNeutralActionable(): number {
    return this.stats?.themeSentiment?.neutral?.count ?? 0;
  }

  /** For Details tab: negative actionable count (overview `theme_sentiment`). */
  getDetailsNegativeActionable(): number {
    return this.stats?.themeSentiment?.negative?.count ?? 0;
  }

  getActionableMentionsSentimentData(): {
    positive: { count: number; percentage: number };
    neutral: { count: number; percentage: number };
    negative: { count: number; percentage: number };
  } {
    return (
      this.stats?.allFeedbackInsights?.sentiment ?? {
        positive: { count: 0, percentage: 0 },
        neutral: { count: 0, percentage: 0 },
        negative: { count: 0, percentage: 0 },
      }
    );
  }

  getActionableMentionsNpsScore(): number {
    return this.stats?.allFeedbackInsights?.npsScore ?? 0;
  }

  getActionableMentionsSentimentScore(): number {
    return this.stats?.allFeedbackInsights?.sentimentScore ?? 0;
  }

  /** All themes from stats (unfiltered). Used for API name resolution; Summary UI uses `cachedSummaryThemes` (non-suggested only). */
  getAllThemes(): Array<{ themeId: string; themeName: string }> {
    const s = this.stats;
    if (!s) return [];
    const str = (id: string | number | null | undefined) => (id == null ? '' : String(id));
    const fromBreakdown = (s.themeSentimentBreakdown ?? []).map((t) => ({ themeId: str(t?.themeId), themeName: t?.themeName ?? '' }));
    if (fromBreakdown.length) return fromBreakdown;
    return (s.themeDistribution ?? []).map((t) => ({ themeId: str(t?.themeId), themeName: t?.themeName ?? '' }));
  }

  /**
   * Theme dropdown items where `is_suggested_theme` is false only.
   * Uses `themeSentimentBreakdown` when present; otherwise falls back to `themeDistribution` (no suggested flag).
   */
  private getNonSuggestedThemeDropdownItems(): Array<{ themeId: string; themeName: string }> {
    const s = this.stats;
    if (!s) return [];
    const str = (id: string | number | null | undefined) => (id == null ? '' : String(id));
    const breakdown = (s.themeSentimentBreakdown ?? []) as ThemeSentimentBreakdown[];
    const nonSuggested = breakdown.filter((t) => t.isSuggestedTheme !== true);
    if (nonSuggested.length) {
      return nonSuggested.map((t) => ({ themeId: str(t?.themeId), themeName: t?.themeName ?? '' }));
    }
    return (s.themeDistribution ?? []).map((t) => ({ themeId: str(t?.themeId), themeName: t?.themeName ?? '' }));
  }

  /** Themes for Category Heatmap dropdown: exclude suggested (`isSuggestedTheme === true`). */
  getHeatmapThemeDropdownItems(): Array<{ themeId: string; themeName: string }> {
    return this.getNonSuggestedThemeDropdownItems();
  }

  /**
   * Drop heatmap rows that match suggested theme/sub-theme in stats breakdown (fallback if API omits filters).
   */
  private filterHeatmapRowsNonSuggested(level: 'theme' | 'subTheme', rows: any[]): any[] {
    const s = this.stats;
    if (!s || !Array.isArray(rows) || !rows.length) return rows;
    if (level === 'theme') {
      const suggested = new Set<string>();
      for (const t of s.themeSentimentBreakdown ?? []) {
        if (t?.isSuggestedTheme === true) {
          if (t.themeId != null) suggested.add(String(t.themeId).trim());
          if (t.themeName != null) suggested.add(String(t.themeName).trim());
        }
      }
      if (!suggested.size) return rows;
      return rows.filter((r) => {
        const id = String(r?.themeId ?? '').trim();
        const name = String(r?.themeName ?? '').trim();
        return !suggested.has(id) && !suggested.has(name);
      });
    }
    const suggestedSubKeys = new Set<string>();
    for (const st of s.subThemeSentimentBreakdown ?? []) {
      if (isSubThemeDistributionRowSuggested(st)) {
        const tid = String(st.themeId ?? '').trim();
        const sn = String(st.subThemeName ?? st.subThemeId ?? '').trim();
        const sid = String(st.subThemeId ?? '').trim();
        if (tid && sn) suggestedSubKeys.add(`${tid}|${sn}`);
        if (tid && sid && sid !== sn) suggestedSubKeys.add(`${tid}|${sid}`);
      }
    }
    if (!suggestedSubKeys.size) return rows;
    return rows.filter((r) => {
      const tid = String(r?.themeId ?? '').trim();
      const sn = String(r?.subThemeName ?? '').trim();
      const sid = String(r?.subThemeId ?? '').trim();
      if (suggestedSubKeys.has(`${tid}|${sn}`)) return false;
      if (sid && suggestedSubKeys.has(`${tid}|${sid}`)) return false;
      return true;
    });
  }

  generateSummary(): void {
    if (!this.brandIdForApi || !this.summarySelectedSentiment || !this.summarySelectedThemeId) {
      return;
    }
    const themes = this.getAllThemes();
    const themeName = themes.find((t) => t.themeId === this.summarySelectedThemeId)?.themeName ?? this.summarySelectedThemeId;
    const sentiment = this.summarySelectedSentiment as 'positive' | 'neutral' | 'negative' | 'all';
    this.summaryGenSub?.unsubscribe();
    this.summaryGenerating = true;
    this.summaryText = null;
    this.summaryStats = null;
    this.summaryTopIssues = [];
    this.summaryNextSteps = [];
    this.cdr.markForCheck();
    this.summaryGenSub = this.statsService
      .generateSummary(
        this.brandIdForApi,
        this.summarySelectedThemeId,
        themeName,
        sentiment,
        this.summaryOnlyActionable,
        this.summaryV2PeriodType
      )
      .subscribe({
        next: (res) => {
          this.summaryGenerating = false;
          if (res?.success && res?.data) {
            this.summaryText = String(res.data.summary ?? '');
            this.summaryStats =
              res.data.feedbackCount != null && res.data.actionableCount != null
                ? { feedbackCount: res.data.feedbackCount, actionableCount: res.data.actionableCount }
                : null;
            this.summaryTopIssues = res.data.topIssues ?? [];
            this.summaryNextSteps = res.data.nextSteps ?? [];
          } else {
            this.summaryText = null;
            this.summaryStats = null;
            this.summaryTopIssues = [];
            this.summaryNextSteps = [];
            if (res?.error) {
              this.showSnackbar(res.error, this.alertKindForMessage(res.error, 'error'));
            }
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.summaryGenerating = false;
          this.summaryText = null;
          this.summaryStats = null;
          this.summaryTopIssues = [];
          this.summaryNextSteps = [];
          this.showSnackbar(this.config.copy.errorLoad, 'error');
          this.cdr.markForCheck();
        },
      });
  }

  /** Standard dashboard popup (`Helpers.smallAlertBox` / toast), not inline snackbar. */
  showSnackbar(message: string, type: 'info' | 'error' | 'warn' = 'info'): void {
    const detail = String(message ?? '').trim() || 'Something went wrong.';
    const kind = type === 'warn' ? 'warn' : this.alertKindForMessage(detail, type);
    let header = 'Information';
    let msgType = 'Success';
    if (kind === 'error') {
      header = 'Error';
      msgType = 'error';
    } else if (kind === 'warn') {
      header = 'Warning';
      msgType = 'warn';
    }
    this.helpersService.smallAlertBox(header, detail, msgType, this.config.snackbarDuration);
  }

  private alertKindForMessage(
    message: string,
    fallback: 'info' | 'error' | 'warn' = 'info',
  ): 'info' | 'error' | 'warn' {
    const m = String(message ?? '').toLowerCase();
    if (/not available|not ready|already in progress|try again (later|shortly)/i.test(m)) {
      return 'warn';
    }
    return fallback;
  }

  /** Used from template when tab children emit (ChangeDetectorRef is private). */
  markForCheckFromTemplate(): void {
    this.cdr.markForCheck();
  }

  loadTrendData(): void {
    if (!this.brandIdForApi) return;
    this.trendLoading = true;
    this.cdr.markForCheck();
    const timePeriod = this.detailsTimePeriod as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    this.statsService
      .getTrendData(
        this.brandIdForApi,
        this.detailsTrendThemeId,
        this.detailsTrendSubThemeId,
        timePeriod
      )
      .subscribe({
        next: (res) => {
          this.trendData = res?.data ?? [];
          this.trendLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.trendLoading = false;
          this.showSnackbar(this.config.copy.errorLoad, 'error');
          this.cdr.markForCheck();
        },
      });
  }

  /** Load categorized feedback and build cohort table for 3rd card (Cohort tab) – same as older code. */
  loadCohortCardData(): void {
    if (!this.brandIdForApi || !this.detailsTrendThemeId) {
      this.cohortCardFeedback = [];
      this.cohortCardTableData = [];
      this.cohortCardTableLoading = false;
      this.cohortCardFeedbackPage = 1;
      this.cohortCardFeedbackHasMore = false;
      this.cohortCardFeedbackLoadingMore = false;
      this.cdr.markForCheck();
      return;
    }
    this.cohortCardTableLoading = true;
    this.cohortCardFeedbackLoadingMore = false;
    this.cohortCardFeedbackPage = 1;
    this.cohortCardFeedbackHasMore = false;
    this.cohortCardFeedback = [];
    this.cohortCardTableData = [];
    this.cdr.markForCheck();
    const themeId = this.detailsTrendThemeId;
    const subThemeId = this.detailsTrendSubThemeId;

    if (this.config.useInsightsEngine) {
      const cohortKey = String(this.detailsCohortCardDimension ?? '').trim();
      this.statsService
        .getCohortBreakdown(this.brandIdForApi, cohortKey, themeId, subThemeId ?? null)
        .subscribe({
          next: (r) => {
            this.cohortCardTableLoading = false;
            this.cohortCardFeedback = [];
            this.cohortCardFeedbackPage = 1;
            this.cohortCardFeedbackHasMore = false;
            if (!r.success) {
              this.cohortCardTableData = [];
              this.showSnackbar(this.config.copy.errorLoad, 'error');
              this.cdr.markForCheck();
              return;
            }
            this.cohortCardTableData = Array.isArray(r.data) ? r.data : [];
            this.cdr.markForCheck();
          },
          error: () => {
            this.cohortCardFeedback = [];
            this.cohortCardTableData = [];
            this.cohortCardTableLoading = false;
            this.showSnackbar(this.config.copy.errorLoad, 'error');
            this.cdr.markForCheck();
          },
        });
      return;
    }

    const obs = subThemeId
      ? this.statsService.getCategorizedFeedbackBySubTheme(this.brandIdForApi, themeId, subThemeId)
      : this.statsService.getCategorizedFeedbackByTheme(this.brandIdForApi, themeId);
    obs.subscribe({
      next: (res) => {
        const list = res?.success && res?.data ? (Array.isArray(res.data) ? res.data : []) : [];
        this.cohortCardFeedback = list;
        this.cohortCardFeedbackHasMore = false;
        this.buildCohortTableFromFeedback();
        this.cohortCardTableLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.cohortCardFeedback = [];
        this.cohortCardTableData = [];
        this.cohortCardTableLoading = false;
        this.showSnackbar(this.config.copy.errorLoad, 'error');
        this.cdr.markForCheck();
      },
    });
  }

  /** Infinite scroll: load next raw-comments page for cohort breakdown (insights engine). */
  onCohortCardScroll(event: Event): void {
    if (!this.config.useInsightsEngine) {
      return;
    }
    const el = event.target as HTMLElement;
    if (!el || this.cohortCardFeedbackLoadingMore || this.cohortCardTableLoading) {
      return;
    }
    if (!this.cohortCardFeedbackHasMore || !this.detailsTrendThemeId) {
      return;
    }
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 100) {
      return;
    }
    const themeId = this.detailsTrendThemeId;
    const subThemeId = this.detailsTrendSubThemeId;
    this.cohortCardFeedbackLoadingMore = true;
    this.cdr.markForCheck();
    const nextPage = this.cohortCardFeedbackPage + 1;
    this.statsService
      .getRawCommentsPage(
        this.brandIdForApi,
        subThemeId ? { theme_name: themeId, subtheme_name: subThemeId } : { theme_name: themeId },
        nextPage
      )
      .subscribe({
        next: (r) => {
          this.cohortCardFeedbackLoadingMore = false;
          if (!r.success) {
            this.cdr.markForCheck();
            return;
          }
          this.cohortCardFeedback = [...this.cohortCardFeedback, ...r.data];
          this.cohortCardFeedbackPage = nextPage;
          this.cohortCardFeedbackHasMore = r.pageInfo?.has_more ?? false;
          this.buildCohortTableFromFeedback();
          this.cdr.markForCheck();
        },
        error: () => {
          this.cohortCardFeedbackLoadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  /** Build cohort table rows from cohortCardFeedback using detailsCohortCardDimension (`field_id` / LPD). */
  private buildCohortTableFromFeedback(): void {
    const list = this.cohortCardFeedback || [];
    const dimension = String(this.detailsCohortCardDimension ?? '').trim();
    if (!dimension) {
      this.cohortCardTableData = [];
      return;
    }
    type Row = { name: string; positive: number; neutral: number; negative: number };
    const map = new Map<string, Row>();
    for (const feedback of list) {
      const sentiment = feedback?.sentiment as 'positive' | 'neutral' | 'negative' | undefined;
      const fb = feedback?.feedback || {};
      let key = 'Unknown';
      const lpd =
        (feedback as any)?.litmus_processed_data ??
        (fb as any)?.litmus_processed_data ??
        {};
      const camel = dimension.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());
      const rawVal =
        (lpd as Record<string, unknown>)[dimension] ?? (lpd as Record<string, unknown>)[camel];
      if (rawVal != null && String(rawVal).trim() !== '') {
        key = String(rawVal).trim();
      } else {
        const fromFb = this.getFeedbackCohortRawValue(feedback, dimension);
        key = fromFb !== '' ? fromFb : 'Unknown';
      }
      if (!map.has(key)) map.set(key, { name: key, positive: 0, neutral: 0, negative: 0 });
      const row = map.get(key)!;
      if (sentiment === 'positive') row.positive += 1;
      else if (sentiment === 'neutral') row.neutral += 1;
      else if (sentiment === 'negative') row.negative += 1;
    }
    const rows = Array.from(map.values());
    rows.sort((a, b) => (b.positive + b.neutral + b.negative) - (a.positive + a.neutral + a.negative));
    this.cohortCardTableData = rows;
  }

  loadHeatmapData(): void {
    if (!this.brandIdForApi) return;
    if (!String(this.detailsHeatmapCohortType ?? '').trim()) {
      this.heatmapData = [];
      this.heatmapLoading = false;
      this.cdr.markForCheck();
      return;
    }
    this.heatmapLoadSub?.unsubscribe();
    this.heatmapData = [];
    this.heatmapLoading = true;
    const loadGeneration = ++this.heatmapLoadGeneration;
    this.cdr.markForCheck();
    const cohortForRequest = String(this.detailsHeatmapCohortType ?? '').trim();
    this.heatmapLoadSub = this.statsService
      .getHeatmapData(
        this.brandIdForApi,
        this.detailsHeatmapSentimentType,
        cohortForRequest,
        this.detailsHeatmapLevel,
        this.detailsHeatmapThemeId
      )
      .subscribe({
        next: (res) => {
          if (loadGeneration !== this.heatmapLoadGeneration) {
            return;
          }
          const d = res?.data;
          let arr: any[] = [];
          if (Array.isArray(d)) {
            arr = d;
          } else if (d != null && typeof d === 'object') {
            const o = d as Record<string, unknown>;
            if (Array.isArray(o['heatmap'])) arr = o['heatmap'];
            else if (Array.isArray(o['rows'])) arr = o['rows'];
            else if (Array.isArray(o['data'])) arr = o['data'];
          }
          this.heatmapData = this.filterHeatmapRowsNonSuggested(this.detailsHeatmapLevel, arr);
          this.heatmapLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (loadGeneration !== this.heatmapLoadGeneration) {
            return;
          }
          this.heatmapLoading = false;
          this.showSnackbar(this.config.copy.errorLoad, 'error');
          this.cdr.markForCheck();
        },
      });
  }

  onHeatmapThemeFilterChange(value: string): void {
    this.detailsHeatmapThemeId = value === 'all' || !value ? null : value;
    this.loadHeatmapData();
  }

  onHeatmapBackToThemes(): void {
    this.detailsHeatmapLevel = 'theme';
    this.detailsHeatmapThemeId = null;
    this.heatmapCellSelected = null;
    this.heatmapFeedbackList = [];
    this.heatmapFeedbackRawAccumulated = [];
    this.heatmapFeedbackAutoPrefetchRounds = 0;
    this.heatmapFeedbackPage = 1;
    this.heatmapFeedbackHasMore = false;
    this.heatmapFeedbackLoadingMore = false;
    this.heatmapFeedbackThemeId = null;
    this.heatmapFeedbackSubThemeId = null;
    this.heatmapFeedbackRawFilters = {};
    this.loadHeatmapData();
    this.cdr.markForCheck();
  }

  onHeatmapLevelFilterChange(value: string): void {
    this.detailsHeatmapLevel = value === 'Sub-themes' ? 'subTheme' : 'theme';
    if (this.detailsHeatmapLevel === 'subTheme') this.detailsHeatmapThemeId = null;
    this.loadHeatmapData();
    this.cdr.markForCheck();
  }

  onHeatmapCohortFilterChange(value: string): void {
    this.detailsHeatmapCohortType = String(value ?? '').trim();
    this.clearHeatmapFeedbackPanelOnly();
    this.loadHeatmapData();
    this.cdr.markForCheck();
  }

  onHeatmapCellClick(payload: HeatmapCellPayload): void {
    this.heatmapCellSelected = payload;
    this.heatmapFeedbackList = [];
    this.heatmapFeedbackRawAccumulated = [];
    this.heatmapFeedbackAutoPrefetchRounds = 0;
    this.heatmapFeedbackPage = 1;
    this.heatmapFeedbackHasMore = false;
    this.heatmapFeedbackLoadingMore = false;
    this.heatmapFeedbackThemeId = null;
    this.heatmapFeedbackSubThemeId = null;
    this.heatmapFeedbackRawFilters = {};
    this.heatmapFeedbackLoading = true;
    this.cdr.markForCheck();
    this.loadHeatmapFeedback(payload);
  }

  /** Match heatmap row label to an item from current heatmap data (ES uses theme/subtheme names). */
  private findHeatmapDataItemForRow(rowLabel: string): Record<string, unknown> | null {
    const level = this.detailsHeatmapLevel;
    const label = String(rowLabel ?? '').trim();
    for (const item of this.heatmapData ?? []) {
      if (level === 'theme') {
        const r = String(
          (item as any)?.themeName ?? (item as any)?.theme_name ?? (item as any)?.themeId ?? ''
        ).trim();
        if (r === label) return item as Record<string, unknown>;
      } else {
        const st = String(
          (item as any)?.subThemeName ?? (item as any)?.subtheme_name ?? (item as any)?.subThemeId ?? ''
        ).trim();
        const dv = String((item as any)?.dimensionValue ?? '').trim();
        if (st === label || dv === label || String((item as any)?.subThemeId ?? '') === label) {
          return item as Record<string, unknown>;
        }
      }
    }
    return null;
  }

  /** Theme name string for raw-comments `filters.theme_name` (must match ES). */
  private resolveThemeNameForHeatmapApi(rowLabel: string): string {
    const themes = this.getAllThemes();
    const match = themes.find((t) => t.themeName === rowLabel || t.themeId === rowLabel);
    if (match?.themeName) return match.themeName;
    if (match?.themeId) return match.themeId;
    const fromData = this.findHeatmapDataItemForRow(rowLabel);
    if (fromData) {
      return String(
        fromData['themeName'] ?? fromData['theme_name'] ?? fromData['themeId'] ?? rowLabel
      ).trim();
    }
    return String(rowLabel).trim();
  }

  /**
   * When heatmap columns are theme sentiment (positive / negative / neutral), pass through to
   * POST .../raw_comments so the API filters in ES; otherwise client-only cohort filters apply.
   */
  private heatmapSentimentLabelsForRawComments(payload: HeatmapCellPayload): string[] | undefined {
    const key = String(payload.columnCohortKey ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
    if (!key) return undefined;
    const themeSentiments = ['positive', 'negative', 'neutral'];
    if (themeSentiments.includes(key)) {
      return [key];
    }
    return undefined;
  }

  /**
   * Heatmap columns can be theme sentiment (positive / negative / neutral). Those keys are passed as
   * `columnCohortKey` but must not be run through cohort-value matching for drill-down feedback.
   */
  private isThemeSentimentColumnCohortKey(key: string): boolean {
    const k = String(key ?? '').trim().toLowerCase();
    return k === 'positive' || k === 'negative' || k === 'neutral';
  }

  /**
   * If nothing in the loaded page has the requested cohort dimension, client-side cohort filtering would
   * drop every row despite a successful API response — skip filtering in that case (insights only).
   */
  private getFeedbackCohortRawValue(fbItem: any, cohortType: string): string {
    const fromLpd = this.getLitmusProcessedCohortFieldValue(fbItem, cohortType);
    if (fromLpd) return fromLpd;
    const fb = fbItem?.feedback ?? fbItem;
    if (!cohortType) return '';
    const camel = cohortType.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());
    const candidates = [cohortType, camel];
    for (const k of candidates) {
      const v = fb?.[k];
      if (v != null && String(v).trim() !== '') return String(v);
      const top = fbItem?.[k];
      if (top != null && String(top).trim() !== '') return String(top);
    }
    return '';
  }

  /** Value for heatmap cohort `field_id` from API `litmus_processed_data`. */
  private getLitmusProcessedCohortFieldValue(fbItem: any, cohortFieldId: string): string {
    const id = String(cohortFieldId ?? '').trim();
    if (!id) return '';
    const lpd =
      fbItem?.litmus_processed_data ??
      fbItem?.feedback?.litmus_processed_data ??
      null;
    if (!lpd || typeof lpd !== 'object' || Array.isArray(lpd)) return '';
    const camel = id.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());
    const raw = lpd[id] ?? lpd[camel];
    if (raw == null) return '';
    return String(raw).trim();
  }

  private insightsRawFeedbackHasCohortDimension(list: any[], cohortType: string): boolean {
    const ct = String(cohortType ?? '').trim();
    if (ct === 'npsCategory' || ct === 'rating_bucket') {
      for (const item of list ?? []) {
        const fb = item?.feedback ?? item;
        const npsRaw = fb?.npsScore ?? fb?.nps ?? item?.npsScore;
        if (npsRaw !== undefined && npsRaw !== null && String(npsRaw).trim() !== '') {
          const n = typeof npsRaw === 'number' ? npsRaw : Number(String(npsRaw).trim());
          if (Number.isFinite(n)) return true;
        }
      }
      return false;
    }
    for (const item of list ?? []) {
      if (String(this.getFeedbackCohortRawValue(item, ct) ?? '').trim() !== '') {
        return true;
      }
    }
    return false;
  }

  private buildHeatmapRawCommentFilters(
    themeName: string,
    subThemeName?: string | null
  ): { theme_name: string; subtheme_name?: string; sentiment_labels?: string[] } {
    const f: { theme_name: string; subtheme_name?: string; sentiment_labels?: string[] } = {
      theme_name: themeName,
    };
    if (subThemeName) {
      f.subtheme_name = subThemeName;
    }
    if (this.heatmapFeedbackRawFilters.sentiment_labels?.length) {
      f.sentiment_labels = this.heatmapFeedbackRawFilters.sentiment_labels;
    }
    return f;
  }

  /** Sub-theme name for raw-comments when sub-theme heatmap row is selected. */
  private resolveSubThemeNameForHeatmapApi(rowLabel: string): string {
    const fromData = this.findHeatmapDataItemForRow(rowLabel);
    if (fromData) {
      return String(
        fromData['subThemeName'] ?? fromData['subtheme_name'] ?? fromData['subThemeId'] ?? rowLabel
      ).trim();
    }
    const breakdown = (this.stats?.subThemeSentimentBreakdown ?? []) as SubThemeSentimentBreakdown[];
    const tid = String(this.detailsHeatmapThemeId ?? '');
    const sub = breakdown.find(
      (s) => String(s.themeId) === tid && (s.subThemeName === rowLabel || s.subThemeId === rowLabel)
    );
    return String(sub?.subThemeName ?? sub?.subThemeId ?? rowLabel).trim();
  }

  private loadHeatmapFeedback(payload: HeatmapCellPayload): void {
    if (!this.brandIdForApi) {
      this.heatmapFeedbackLoading = false;
      this.cdr.markForCheck();
      return;
    }
    const labels = this.heatmapSentimentLabelsForRawComments(payload);
    this.heatmapFeedbackRawFilters = labels?.length ? { sentiment_labels: labels } : {};

    const rowLabel = payload.rowLabel ?? '';
    const themes = this.getAllThemes();
    if (this.detailsHeatmapLevel === 'theme') {
      const themeNameForApi = this.resolveThemeNameForHeatmapApi(rowLabel);
      const legacyThemeId = themes.find((t) => t.themeName === rowLabel || t.themeId === rowLabel)?.themeId ?? rowLabel;
      this.heatmapFeedbackThemeId = themeNameForApi;
      this.heatmapFeedbackSubThemeId = null;
      if (this.config.useInsightsEngine) {
        this.statsService
          .getRawCommentsPage(this.brandIdForApi, this.buildHeatmapRawCommentFilters(themeNameForApi), 1)
          .subscribe({
            next: (r) => this.onHeatmapRawCommentsPage(r),
            error: () => this.setHeatmapFeedbackError(),
          });
        return;
      }
      this.statsService.getCategorizedFeedbackByTheme(this.brandIdForApi, legacyThemeId).subscribe({
        next: (res) => this.mapAndSetHeatmapFeedback(res),
        error: () => this.setHeatmapFeedbackError(),
      });
      return;
    }
    const themeIdSelected = this.detailsHeatmapThemeId;
    if (!themeIdSelected) {
      this.heatmapFeedbackLoading = false;
      this.cdr.markForCheck();
      return;
    }
    const parentTheme = themes.find((t) => t.themeId === String(themeIdSelected));
    const themeNameForApi = parentTheme?.themeName ?? parentTheme?.themeId ?? String(themeIdSelected);
    const subThemeNameForApi = this.resolveSubThemeNameForHeatmapApi(rowLabel);
    const breakdown = (this.stats?.subThemeSentimentBreakdown ?? []) as SubThemeSentimentBreakdown[];
    const sub = breakdown.find(
      (s) => s.themeId === themeIdSelected && (s.subThemeName === rowLabel || s.subThemeId === rowLabel)
    );
    const legacySubId = sub?.subThemeId ?? rowLabel;
    this.heatmapFeedbackThemeId = themeNameForApi;
    this.heatmapFeedbackSubThemeId = subThemeNameForApi;
    if (this.config.useInsightsEngine) {
      this.statsService
        .getRawCommentsPage(
          this.brandIdForApi,
          this.buildHeatmapRawCommentFilters(themeNameForApi, subThemeNameForApi),
          1
        )
        .subscribe({
          next: (r) => this.onHeatmapRawCommentsPage(r),
          error: () => this.setHeatmapFeedbackError(),
        });
      return;
    }
    this.statsService.getCategorizedFeedbackBySubTheme(this.brandIdForApi, themeIdSelected, legacySubId).subscribe({
      next: (res) => this.mapAndSetHeatmapFeedback(res),
      error: () => this.setHeatmapFeedbackError(),
    });
  }

  /** Insights engine: append page of raw comments for heatmap drill-down. */
  onHeatmapFeedbackLoadMore(): void {
    if (
      !this.config.useInsightsEngine ||
      !this.heatmapFeedbackHasMore ||
      this.heatmapFeedbackLoadingMore ||
      this.heatmapFeedbackLoading ||
      !this.brandIdForApi ||
      !this.heatmapFeedbackThemeId
    ) {
      return;
    }
    this.heatmapFeedbackLoadingMore = true;
    this.cdr.markForCheck();
    const nextPage = this.heatmapFeedbackPage + 1;
    const tid = this.heatmapFeedbackThemeId;
    const sid = this.heatmapFeedbackSubThemeId;
    this.statsService
      .getRawCommentsPage(
        this.brandIdForApi,
        sid ? this.buildHeatmapRawCommentFilters(tid, sid) : this.buildHeatmapRawCommentFilters(tid),
        nextPage
      )
      .subscribe({
        next: (r) => {
          this.heatmapFeedbackLoadingMore = false;
          if (!r.success) {
            this.cdr.markForCheck();
            return;
          }
          this.heatmapFeedbackRawAccumulated = [...this.heatmapFeedbackRawAccumulated, ...r.data];
          this.heatmapFeedbackPage = r.pageInfo?.page ?? nextPage;
          this.heatmapFeedbackHasMore = r.pageInfo?.has_more ?? false;
          this.rebuildHeatmapFeedbackFromAccumulated();
          this.scheduleHeatmapFeedbackAutoPrefetch();
          this.cdr.markForCheck();
        },
        error: () => {
          this.heatmapFeedbackLoadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  private onHeatmapRawCommentsPage(r: {
    success: boolean;
    data: any[];
    pageInfo: RawCommentsPageInfo | null;
  }): void {
    if (!r.success) {
      this.setHeatmapFeedbackError();
      return;
    }
    this.heatmapFeedbackRawAccumulated = [...this.heatmapFeedbackRawAccumulated, ...r.data];
    this.heatmapFeedbackPage = r.pageInfo?.page ?? 1;
    this.heatmapFeedbackHasMore = r.pageInfo?.has_more ?? false;
    this.rebuildHeatmapFeedbackFromAccumulated();
    this.heatmapFeedbackLoading = false;
    this.heatmapFeedbackLoadingMore = false;
    this.cdr.markForCheck();
    this.scheduleHeatmapFeedbackAutoPrefetch();
  }

  /** After each raw-comments page, pull more pages if cohort + sentiment filters leave too few visible rows. */
  private scheduleHeatmapFeedbackAutoPrefetch(): void {
    queueMicrotask(() => this.runHeatmapFeedbackAutoPrefetch());
  }

  private heatmapFeedbackAutoPrefetchTarget(): number {
    return Math.max(20, this.config.rawCommentsPageSize);
  }

  private runHeatmapFeedbackAutoPrefetch(): void {
    if (!this.config.useInsightsEngine) return;
    if (!this.brandIdForApi || !this.heatmapFeedbackThemeId) return;
    if (this.heatmapFeedbackLoading || this.heatmapFeedbackLoadingMore) return;
    if (!this.heatmapFeedbackHasMore) return;
    if (this.heatmapFeedbackAutoPrefetchRounds >= this.heatmapFeedbackAutoPrefetchMaxRounds) return;

    const cell = this.heatmapCellSelected;
    const cohortKey = cell?.columnCohortKey;
    if (cohortKey == null || cohortKey === '') return;
    if (this.isThemeSentimentColumnCohortKey(String(cohortKey))) return;

    const visible = this.applyHeatmapFeedbackFilters(this.heatmapFeedbackRawAccumulated).length;
    if (visible >= this.heatmapFeedbackAutoPrefetchTarget()) return;

    this.heatmapFeedbackAutoPrefetchRounds += 1;
    this.heatmapFeedbackLoadingMore = true;
    this.cdr.markForCheck();

    const nextPage = this.heatmapFeedbackPage + 1;
    const tid = this.heatmapFeedbackThemeId;
    const sid = this.heatmapFeedbackSubThemeId;
    this.statsService
      .getRawCommentsPage(
        this.brandIdForApi,
        sid ? this.buildHeatmapRawCommentFilters(tid, sid) : this.buildHeatmapRawCommentFilters(tid),
        nextPage
      )
      .subscribe({
        next: (r) => {
          this.heatmapFeedbackLoadingMore = false;
          if (!r.success) {
            this.cdr.markForCheck();
            return;
          }
          this.heatmapFeedbackRawAccumulated = [...this.heatmapFeedbackRawAccumulated, ...r.data];
          this.heatmapFeedbackPage = r.pageInfo?.page ?? nextPage;
          this.heatmapFeedbackHasMore = r.pageInfo?.has_more ?? false;
          this.rebuildHeatmapFeedbackFromAccumulated();
          this.scheduleHeatmapFeedbackAutoPrefetch();
          this.cdr.markForCheck();
        },
        error: () => {
          this.heatmapFeedbackLoadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  private applyHeatmapFeedbackFilters(rawList: any[]): HeatmapFeedbackItem[] {
    const sentimentMode = this.detailsHeatmapSentimentType;
    const cohortKey = this.heatmapCellSelected?.columnCohortKey ?? '';
    const cohortType = this.detailsHeatmapCohortType;
    const matchesSentiment = (f: any) => {
      const s = (f?.sentiment ?? f?.feedback?.sentiment ?? '').toLowerCase();
      /** Cell is a theme-sentiment column; list must match that column, not the heatmap Negative/Non-negative toggle. */
      if (this.isThemeSentimentColumnCohortKey(cohortKey)) {
        return s === String(cohortKey ?? '').trim().toLowerCase();
      }
      if (sentimentMode === 'negative') return s === 'negative';
      return s === 'positive' || s === 'neutral';
    };
    const matchesCohort = (f: any) => this.heatmapFeedbackMatchesCohort(f, cohortKey, cohortType);
    const bySentiment = rawList.filter(matchesSentiment);
    const applyCohortFilter =
      !!cohortKey &&
      !this.isThemeSentimentColumnCohortKey(cohortKey) &&
      !(this.config.useInsightsEngine && !this.insightsRawFeedbackHasCohortDimension(rawList, cohortType));
    const byCohort = applyCohortFilter ? bySentiment.filter(matchesCohort) : bySentiment;
    return byCohort.map((f: any) => {
      const id = String(f?.feed_id ?? f?.feedback?.feed_id ?? '').trim();
      return {
        sentiment: (f?.sentiment ?? f?.feedback?.sentiment ?? '') || 'neutral',
        text: f?.feedback?.primaryComment ?? f?.primaryComment ?? f?.text ?? '',
        cohortTagsFooter: this.buildHeatmapFeedbackCohortTagsFooter(f),
        feedId: id || undefined,
      };
    });
  }

  /**
   * Customer Feedback footer: one segment per cohort dimension from brand options, excluding `rating_bucket`.
   */
  private buildHeatmapFeedbackCohortTagsFooter(fbItem: any): Array<{ label: string; value: string }> {
    const out: Array<{ label: string; value: string }> = [];
    for (const opt of this.heatmapCohortOptions) {
      if (opt.value === 'rating_bucket') continue;
      const raw = this.resolveCohortTagDisplayValue(fbItem, opt.value);
      const value = String(raw ?? '').trim() || '—';
      out.push({ label: String(opt.label || opt.value).trim() || opt.value, value });
    }
    return out;
  }

  /** Theme / sub-theme Customer Feedback: same cohort chips as Category Heatmap panel. */
  private panelFeedbackWithCohortTags(list: any[]): any[] {
    if (!list?.length) return [];
    return list.map((item) => ({
      ...item,
      cohortTagsFooter: this.buildHeatmapFeedbackCohortTagsFooter(item),
    }));
  }

  /** Resolve display string for a cohort field (`field_id` in LPD or on feedback). */
  private resolveCohortTagDisplayValue(fbItem: any, fieldId: string): string {
    const id = String(fieldId ?? '').trim();
    if (!id) return '';
    const lpd = this.getLitmusProcessedCohortFieldValue(fbItem, id);
    if (lpd) return lpd;
    const raw = this.getFeedbackCohortRawValue(fbItem, id);
    if (raw) return raw;
    return '';
  }

  private rebuildHeatmapFeedbackFromAccumulated(): void {
    this.heatmapFeedbackList = this.applyHeatmapFeedbackFilters(this.heatmapFeedbackRawAccumulated);
  }

  private mapAndSetHeatmapFeedback(res: { success?: boolean; data?: any[] }): void {
    const list = res?.success && Array.isArray(res?.data) ? res.data : [];
    this.heatmapFeedbackList = this.applyHeatmapFeedbackFilters(list);
    this.heatmapFeedbackLoading = false;
    this.cdr.markForCheck();
  }

  /** Match feedback item to heatmap cell cohort (NPS / rating bucket, or dynamic `field_id` via LPD / feedback). */
  private heatmapFeedbackMatchesCohort(fbItem: any, cohortNorm: string, cohortType: string): boolean {
    const fb = fbItem?.feedback ?? fbItem;
    const norm = (v: string | null | undefined) => String(v ?? 'unknown').trim().toLowerCase().replace(/\s+/g, '');
    if (cohortType === 'npsCategory' || cohortType === 'rating_bucket') {
      if (
        cohortType === 'rating_bucket' &&
        (cohortNorm === 'top_2_box' || cohortNorm === 'middle_bucket' || cohortNorm === 'bottom_2_box')
      ) {
        const num = (v: any) =>
          v === undefined || v === null || v === ''
            ? NaN
            : typeof v === 'number'
              ? v
              : Number(String(v).trim());
        const rating = num(
          fb?.npsScore ??
            fb?.nps ??
            (fb as any)?.primary_rating ??
            (fb as any)?.nps_score ??
            (fb as any)?.nps_value ??
            fbItem?.npsScore ??
            fbItem?.nps
        );
        if (!Number.isFinite(rating)) return false;
        if (cohortNorm === 'top_2_box') return rating >= 4 && rating <= 5;
        if (cohortNorm === 'middle_bucket') return rating === 3;
        if (cohortNorm === 'bottom_2_box') return rating >= 1 && rating <= 2;
        return false;
      }
      const nps = (v: any) => (v === undefined || v === null || v === '' ? null : typeof v === 'number' ? v : Number(String(v).trim()));
      const npsScore = nps(fb?.npsScore ?? fb?.nps ?? (fb as any)?.nps_score ?? (fb as any)?.nps_value ?? fbItem?.npsScore ?? fbItem?.nps) ?? null;
      const npsCatFromScore = typeof npsScore === 'number'
        ? (npsScore <= 6 ? 'detractor' : npsScore <= 8 ? 'passive' : 'promoter')
        : null;
      const rawCat = (fb?.npsCategory ?? fbItem?.npsCategory ?? '').toString().trim().toLowerCase();
      let npsCat = npsCatFromScore;
      if (rawCat.startsWith('promot')) npsCat = 'promoter';
      else if (rawCat.startsWith('passiv')) npsCat = 'passive';
      else if (rawCat.startsWith('detract')) npsCat = 'detractor';
      if (!npsCat) {
        const ref = String(fb?.referenceId ?? fbItem?.referenceId ?? '').trim();
        const m = ref.match(/(\d)(?!.*\d)/);
        const digit = m ? Number(m[1]) : 0;
        const seed = Number.isFinite(digit) ? digit : 0;
        const mod = seed % 3;
        npsCat = mod === 0 ? 'detractor' : mod === 1 ? 'passive' : 'promoter';
      }
      return (npsCat ?? 'unknown') === cohortNorm;
    }
    const v = this.getFeedbackCohortRawValue(fbItem, cohortType);
    return norm(v) === cohortNorm;
  }

  private setHeatmapFeedbackError(): void {
    this.heatmapFeedbackList = [];
    this.heatmapFeedbackRawAccumulated = [];
    this.heatmapFeedbackRawFilters = {};
    this.heatmapFeedbackLoading = false;
    this.heatmapFeedbackLoadingMore = false;
    this.heatmapFeedbackHasMore = false;
    this.cdr.markForCheck();
  }

  /** Info icon click: open side panel and show this theme as selected in Theme break-up and Sub-theme break-up cards. */
  openThemeBreakupPanel(theme: ThemeBreakupItem): void {
    this.saveScrollPosition();
    this.detailsSubthemeSelectedThemeId = theme?.themeId ?? this.detailsSubthemeSelectedThemeId;
    this.detailsTrendSubThemeId = null;
    this.themeBreakupPanelTheme = theme;
    this.themeBreakupPanelSubThemes = theme?.themeId
      ? (this.stats?.subThemeSentimentBreakdown ?? []).filter((s) => s.themeId === theme.themeId)
      : [];
    this.themePanelShowSuggestedSubthemesOnly = false;
    if (this.shouldAutoThemePanelSuggestedSubthemesMode()) {
      this.themePanelShowSuggestedSubthemesOnly = true;
    }
    this.themeBreakupPanelOpen = true;
    this.loadThemePanelFeedback(theme.themeId);
    this.cdr.markForCheck();
    this.restoreScrollPosition();
  }

  /**
   * When every sub-theme for this theme is suggested (no non-suggested rows), default list would be empty —
   * same as Details Sub-theme break-up: turn on suggested view automatically.
   */
  private shouldAutoThemePanelSuggestedSubthemesMode(): boolean {
    const rows = this.themeBreakupPanelSubThemes ?? [];
    if (!rows.length) return false;
    if (rows.some((s) => !isSubThemeDistributionRowSuggested(s))) return false;
    return rows.some((s) => isSubThemeDistributionRowSuggested(s));
  }

  /** At least one suggested sub-theme in the theme panel list — controls Suggested toggle visibility. */
  get themePanelHasSuggestedSubthemes(): boolean {
    return (this.themeBreakupPanelSubThemes ?? []).some((s) => isSubThemeDistributionRowSuggested(s));
  }

  /** Sub-themes shown in the theme side panel after applying the Suggested toggle. */
  get themeBreakupPanelSubThemesForDisplay(): SubThemeSentimentBreakdown[] {
    return (this.themeBreakupPanelSubThemes ?? []).filter((s) =>
      this.themePanelShowSuggestedSubthemesOnly
        ? isSubThemeDistributionRowSuggested(s)
        : !isSubThemeDistributionRowSuggested(s),
    );
  }

  toggleThemePanelSubthemesSuggested(): void {
    this.themePanelShowSuggestedSubthemesOnly = !this.themePanelShowSuggestedSubthemesOnly;
    this.cdr.markForCheck();
  }

  trackBySubThemeId(_index: number, s: SubThemeSentimentBreakdown): string {
    return s?.subThemeId ?? '';
  }

  trackByPanelFeedbackIndex(index: number): number {
    return index;
  }

  /** Bar click: sync theme to Sub-theme break-up and Trend/Cohort cards only (no side panel). */
  syncThemeToCards(theme: ThemeBreakupItem): void {
    this.detailsSubthemeSelectedThemeId = theme.themeId;
    this.detailsTrendThemeId = theme.themeId;
    this.detailsTrendSubThemeId = null;
    this.loadTrendData();
    if (this.detailsTrendCohortTab === 'cohort') this.loadCohortCardData();
    this.cdr.markForCheck();
  }

  /** Theme break-up "Suggested" filter hid the selected theme – clear Details theme / trend selection. */
  onDetailsThemeHiddenBySuggestedFilter(): void {
    this.detailsSubthemeSelectedThemeId = '';
    this.detailsTrendThemeId = null;
    this.detailsTrendSubThemeId = null;
    this.cdr.markForCheck();
  }

  /** Sub-theme break-up "Suggested" filter hid the selected sub-theme – clear trend sub-theme only. */
  onDetailsSubThemeHiddenBySuggestedFilter(): void {
    this.detailsTrendSubThemeId = null;
    this.cdr.markForCheck();
  }

  /** Sub-theme bar click: sync sub-theme to Trend/Cohort card (3rd card) and show Cohort tab. */
  syncSubThemeToTrendCard(payload: { themeId: string; subThemeId: string }): void {
    this.detailsTrendThemeId = payload.themeId;
    this.detailsTrendSubThemeId = payload.subThemeId;
    this.detailsTrendCohortTab = 'cohort'; // Show Cohort-wise break-up when sub-theme is selected
    this.loadTrendData();
    this.loadCohortCardData(); // Load cohort data for 3rd card
    this.cdr.markForCheck();
  }

  onTrendCohortTabChange(tab: 'trend' | 'cohort'): void {
    this.detailsTrendCohortTab = tab;
    if (tab === 'trend') {
      this.loadTrendData();
    } else if (tab === 'cohort' && (this.detailsTrendThemeId || this.detailsTrendSubThemeId)) {
      this.loadCohortCardData();
    }
    this.cdr.markForCheck();
  }

  /** Cohort tab dropdown: change cohort dimension and reload aggregated breakdown (insights) or rebuild from feedback (legacy). */
  onCohortFilterChangeFromCard(value: string): void {
    const v = String(value ?? '').trim();
    if (!this.cohortBreakdownDropdownOptions.some((o) => o.value === v)) {
      return;
    }
    this.detailsCohortCardDimension = v;
    if (this.config.useInsightsEngine) {
      if (this.detailsTrendCohortTab === 'cohort' && (this.detailsTrendThemeId || this.detailsTrendSubThemeId)) {
        this.loadCohortCardData();
      }
    } else {
      this.buildCohortTableFromFeedback();
    }
    this.cdr.markForCheck();
  }

  /** Label for current cohort dimension (matches dropdown so table header and dropdown stay in sync). */
  getCohortCardDimensionLabel(): string {
    const opt = this.cohortBreakdownDropdownOptions.find((o) => o.value === this.detailsCohortCardDimension);
    return opt?.label ?? 'Cohort';
  }

  closeThemeBreakupPanel(): void {
    this.themeBreakupPanelOpen = false;
    this.themeBreakupPanelTheme = null;
    this.themePanelShowSuggestedSubthemesOnly = false;
    this.themePanelFeedback = [];
    this.themePanelFeedbackHasMore = false;
    this.themePanelFeedbackLoadingMore = false;
    this.themePanelFeedbackPage = 1;
    this.themePanelFeedbackTotalCount = null;
    this.cdr.markForCheck();
    this.restoreScrollPosition();
  }

  /** Info icon click in Sub-theme break-up: open side panel and show this sub-theme as selected in Sub-theme break-up card. */
  openSubThemeBreakupPanel(subTheme: SubThemeBarRow): void {
    if (!this.themeBreakupPanelOpen) this.saveScrollPosition();
    this.subThemeBreakupPanelThemeId =
      this.themeBreakupPanelTheme?.themeId ?? this.detailsSubthemeSelectedThemeId ?? null;
    this.subThemeBreakupPanelSubTheme = subTheme;
    this.subThemeBreakupPanelOpen = true;
    this.detailsTrendSubThemeId = subTheme?.subThemeId ?? this.detailsTrendSubThemeId;
    if (this.themeBreakupPanelOpen && this.themeBreakupPanelTheme?.themeId) {
      this.detailsSubthemeSelectedThemeId = this.themeBreakupPanelTheme.themeId;
    }
    if (this.subThemeBreakupPanelThemeId && subTheme?.subThemeId) {
      this.loadSubThemePanelFeedback(this.subThemeBreakupPanelThemeId, subTheme.subThemeId);
    }
    this.cdr.markForCheck();
    if (!this.themeBreakupPanelOpen) this.restoreScrollPosition();
  }

  /** Convert SubThemeSentimentBreakdown to SubThemeBarRow (same shape as details-subtheme-breakup). */
  subThemeBreakdownToBarRow(s: SubThemeSentimentBreakdown): SubThemeBarRow {
    const p = s.sentiment?.positive?.count ?? 0;
    const n = s.sentiment?.neutral?.count ?? 0;
    const neg = s.sentiment?.negative?.count ?? 0;
    const total = p + n + neg || 1;
    return {
      subThemeId: s.subThemeId,
      subThemeName: s.subThemeName,
      positive: (p / total) * 100,
      neutral: (n / total) * 100,
      negative: (neg / total) * 100,
      positiveCount: p,
      neutralCount: n,
      negativeCount: neg,
    };
  }

  /** Info icon click inside Theme break-up side panel: open Sub-theme break-up side panel (nested). */
  openSubThemeBreakupPanelFromThemePanel(subTheme: SubThemeSentimentBreakdown): void {
    this.openSubThemeBreakupPanel(this.subThemeBreakdownToBarRow(subTheme));
  }

  closeSubThemeBreakupPanel(): void {
    this.subThemeBreakupPanelOpen = false;
    this.subThemeBreakupPanelSubTheme = null;
    this.subThemeBreakupPanelThemeId = null;
    this.subThemePanelFeedback = [];
    this.subThemePanelFeedbackHasMore = false;
    this.subThemePanelFeedbackLoadingMore = false;
    this.subThemePanelFeedbackPage = 1;
    this.subThemePanelFeedbackTotalCount = null;
    this.cdr.markForCheck();
    this.restoreScrollPosition();
  }

  private saveScrollPosition(): void {
    if (typeof document === 'undefined') return;
    const doc = document.documentElement;
    const win = typeof window !== 'undefined' ? window : null;
    let scrollContainerTop = 0;
    const container = document.getElementById(this.mainScrollContainerId);
    if (container) {
      scrollContainerTop = container.scrollTop;
    }
    this.savedScrollPosition = {
      x: win ? (win.scrollX ?? doc.scrollLeft) : 0,
      y: win ? (win.scrollY ?? doc.scrollTop) : 0,
      scrollContainerTop,
    };
  }

  private restoreScrollPosition(): void {
    if (typeof document === 'undefined') return;
    const { x, y, scrollContainerTop } = this.savedScrollPosition;
    const container = document.getElementById(this.mainScrollContainerId);
    const scrollToSaved = (): void => {
      if (container) {
        container.scrollTop = scrollContainerTop;
      }
      if (typeof window !== 'undefined') {
        window.scrollTo(x, y);
      }
    };
    requestAnimationFrame(scrollToSaved);
    setTimeout(scrollToSaved, 0);
    setTimeout(scrollToSaved, 50);
    setTimeout(scrollToSaved, 150);
    setTimeout(scrollToSaved, 300);
  }

  getTotalMentionsForTheme(theme: ThemeBreakupItem | null): number {
    if (!theme) return 0;
    return (theme.positiveCount ?? 0) + (theme.neutralCount ?? 0) + (theme.negativeCount ?? 0);
  }

  getTotalMentionsForSubTheme(subTheme: SubThemeBarRow | null): number {
    if (!subTheme) return 0;
    return (subTheme.positiveCount ?? 0) + (subTheme.neutralCount ?? 0) + (subTheme.negativeCount ?? 0);
  }

  private getFeedbackEpochMs(feedback: any): number {
    const candidates = [
      feedback?.createdAt,
      feedback?.feedback?.createdAt,
      feedback?.feedback?.requestDate,
      feedback?.feedback?.requestTime,
      feedback?.categorizedAt,
      feedback?.updatedAt,
    ];
    for (const c of candidates) {
      if (c == null) continue;
      if (typeof c === 'number' && Number.isFinite(c)) return c;
      let s = String(c).trim();
      s = s.replace(/\s+IST$/i, '').trim();
      s = s.replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/, '$1T$2');
      const ms = Date.parse(s);
      if (!Number.isNaN(ms)) return ms;
    }
    return 0;
  }

  private sortFeedbackDesc<T>(items: T[]): T[] {
    return (Array.isArray(items) ? items.slice() : []).sort(
      (a: any, b: any) => this.getFeedbackEpochMs(b) - this.getFeedbackEpochMs(a)
    );
  }

  /** Theme verbatim list: scroll near bottom loads next page (insights engine). */
  onThemePanelFeedbackScroll(event: Event): void {
    if (!this.config.useInsightsEngine) {
      return;
    }
    const el = event.target as HTMLElement;
    if (
      !el ||
      this.themePanelFeedbackLoading ||
      this.themePanelFeedbackLoadingMore ||
      !this.themePanelFeedbackHasMore
    ) {
      return;
    }
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 120) {
      return;
    }
    this.appendThemePanelFeedback();
  }

  private appendThemePanelFeedback(): void {
    const themeId = this.themeBreakupPanelTheme?.themeId;
    if (!this.brandIdForApi || !themeId || !this.themePanelFeedbackHasMore) {
      return;
    }
    this.themePanelFeedbackLoadingMore = true;
    this.cdr.markForCheck();
    const nextPage = this.themePanelFeedbackPage + 1;
    this.statsService.getRawCommentsPage(this.brandIdForApi, { theme_name: themeId }, nextPage).subscribe({
      next: (r) => {
        this.themePanelFeedbackLoadingMore = false;
        if (!r.success) {
          this.cdr.markForCheck();
          return;
        }
        const merged = [...this.themePanelFeedback, ...r.data];
        this.themePanelFeedback = this.panelFeedbackWithCohortTags(this.sortFeedbackDesc(merged));
        this.themePanelFeedbackPage = nextPage;
        this.themePanelFeedbackHasMore = r.pageInfo?.has_more ?? false;
        if (r.pageInfo?.total_count != null) {
          this.themePanelFeedbackTotalCount = r.pageInfo.total_count;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.themePanelFeedbackLoadingMore = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** Sub-theme verbatim list: infinite scroll (insights engine). */
  onSubThemePanelFeedbackScroll(event: Event): void {
    if (!this.config.useInsightsEngine) {
      return;
    }
    const el = event.target as HTMLElement;
    if (
      !el ||
      this.subThemePanelFeedbackLoading ||
      this.subThemePanelFeedbackLoadingMore ||
      !this.subThemePanelFeedbackHasMore
    ) {
      return;
    }
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 120) {
      return;
    }
    this.appendSubThemePanelFeedback();
  }

  private appendSubThemePanelFeedback(): void {
    const themeId = this.subThemeBreakupPanelThemeId;
    const subId = this.subThemeBreakupPanelSubTheme?.subThemeId;
    if (!this.brandIdForApi || !themeId || !subId || !this.subThemePanelFeedbackHasMore) {
      return;
    }
    this.subThemePanelFeedbackLoadingMore = true;
    this.cdr.markForCheck();
    const nextPage = this.subThemePanelFeedbackPage + 1;
    this.statsService
      .getRawCommentsPage(this.brandIdForApi, { theme_name: themeId, subtheme_name: subId }, nextPage)
      .subscribe({
        next: (r) => {
          this.subThemePanelFeedbackLoadingMore = false;
          if (!r.success) {
            this.cdr.markForCheck();
            return;
          }
          const merged = [...this.subThemePanelFeedback, ...r.data];
          this.subThemePanelFeedback = this.panelFeedbackWithCohortTags(this.sortFeedbackDesc(merged));
          this.subThemePanelFeedbackPage = nextPage;
          this.subThemePanelFeedbackHasMore = r.pageInfo?.has_more ?? false;
          if (r.pageInfo?.total_count != null) {
            this.subThemePanelFeedbackTotalCount = r.pageInfo.total_count;
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.subThemePanelFeedbackLoadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadThemePanelFeedback(themeId: string): void {
    if (!this.brandIdForApi || !themeId) return;
    this.themePanelFeedbackLoading = true;
    this.themePanelFeedbackLoadingMore = false;
    this.themePanelFeedbackHasMore = false;
    this.themePanelFeedbackPage = 1;
    this.themePanelFeedbackTotalCount = null;
    this.themePanelFeedback = [];
    this.cdr.markForCheck();

    if (this.config.useInsightsEngine) {
      this.statsService.getRawCommentsPage(this.brandIdForApi, { theme_name: themeId }, 1).subscribe({
        next: (r) => {
          this.themePanelFeedbackLoading = false;
          if (!r.success) {
            this.themePanelFeedback = [];
            this.cdr.markForCheck();
            return;
          }
          this.themePanelFeedback = r.data.length
            ? this.panelFeedbackWithCohortTags(this.sortFeedbackDesc(r.data))
            : [];
          this.themePanelFeedbackPage = 1;
          this.themePanelFeedbackHasMore = r.pageInfo?.has_more ?? false;
          this.themePanelFeedbackTotalCount =
            r.pageInfo?.total_count != null ? r.pageInfo.total_count : null;
          this.cdr.markForCheck();
        },
        error: () => {
          this.themePanelFeedback = [];
          this.themePanelFeedbackLoading = false;
          this.cdr.markForCheck();
        },
      });
      return;
    }

    this.statsService.getCategorizedFeedbackByTheme(this.brandIdForApi, themeId).subscribe({
      next: (response) => {
        this.themePanelFeedback = response?.success && response?.data
          ? this.panelFeedbackWithCohortTags(this.sortFeedbackDesc(response.data))
          : [];
        this.themePanelFeedbackLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.themePanelFeedback = [];
        this.themePanelFeedbackLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadSubThemePanelFeedback(themeId: string, subThemeId: string): void {
    if (!this.brandIdForApi || !themeId || !subThemeId) return;
    this.subThemePanelFeedbackLoading = true;
    this.subThemePanelFeedbackLoadingMore = false;
    this.subThemePanelFeedbackHasMore = false;
    this.subThemePanelFeedbackPage = 1;
    this.subThemePanelFeedbackTotalCount = null;
    this.subThemePanelFeedback = [];
    this.cdr.markForCheck();

    if (this.config.useInsightsEngine) {
      this.statsService
        .getRawCommentsPage(this.brandIdForApi, { theme_name: themeId, subtheme_name: subThemeId }, 1)
        .subscribe({
          next: (r) => {
            this.subThemePanelFeedbackLoading = false;
            if (!r.success) {
              this.subThemePanelFeedback = [];
              this.cdr.markForCheck();
              return;
            }
            this.subThemePanelFeedback = r.data.length
              ? this.panelFeedbackWithCohortTags(this.sortFeedbackDesc(r.data))
              : [];
            this.subThemePanelFeedbackPage = 1;
            this.subThemePanelFeedbackHasMore = r.pageInfo?.has_more ?? false;
            this.subThemePanelFeedbackTotalCount =
              r.pageInfo?.total_count != null ? r.pageInfo.total_count : null;
            this.cdr.markForCheck();
          },
          error: () => {
            this.subThemePanelFeedback = [];
            this.subThemePanelFeedbackLoading = false;
            this.cdr.markForCheck();
          },
        });
      return;
    }

    this.statsService
      .getCategorizedFeedbackBySubTheme(this.brandIdForApi, themeId, subThemeId)
      .subscribe({
        next: (response) => {
          this.subThemePanelFeedback = response?.success && response?.data
            ? this.panelFeedbackWithCohortTags(this.sortFeedbackDesc(response.data))
            : [];
          this.subThemePanelFeedbackLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.subThemePanelFeedback = [];
          this.subThemePanelFeedbackLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getDetailsTotalFeedback(): number {
    const s = this.stats?.allFeedbackInsights?.sentiment;
    if (!s) return 0;
    return (s.positive?.count ?? 0) + (s.neutral?.count ?? 0) + (s.negative?.count ?? 0);
  }

  showFeedDetails(feed_id: string): void {
    this._ltmsEvent.emit('feed-detail-panel', {
      feed_id: feed_id,
      feedIdList:[],
      pagingDetails:{
        start: 0,
        end: 0,
        pageSize: 10,
        pageNumber: 1,
        total: 0,
        totalpages: 1
      }
    })

  }
}
